import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Building, Hash, Loader2 } from "lucide-react";
import { useAddressAutocomplete, AddressSuggestion } from "@/hooks/use-address-autocomplete";
import { cn } from "@/lib/utils";

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  address: <MapPin className="w-4 h-4" />,
  city: <Building className="w-4 h-4" />,
  zip: <Hash className="w-4 h-4" />,
  county: <Building className="w-4 h-4" />,
  neighborhood: <MapPin className="w-4 h-4" />,
};

const typeLabels: Record<string, string> = {
  address: "Address",
  city: "City",
  zip: "ZIP",
  county: "County",
  neighborhood: "Neighborhood",
};

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Search by address, city, or zip...",
  className,
}: AddressAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // The list scrolls, so arrow-key navigation has to bring the highlighted row
  // into view or the selection walks off the bottom invisibly.
  useEffect(() => {
    if (highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const { suggestions, isLoading, error, fetchSuggestions, clearSuggestions } =
    useAddressAutocomplete();

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    fetchSuggestions(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion: AddressSuggestion) => {
    // Format the display value based on type
    let displayValue = suggestion.text;
    if (suggestion.type === "city" && suggestion.state) {
      displayValue = `${suggestion.city}, ${suggestion.state}`;
    } else if (suggestion.type === "zip") {
      displayValue = suggestion.zip;
    }
    
    onChange(displayValue);
    clearSuggestions();
    setIsOpen(false);
    onSelect?.(suggestion);
    inputRef.current?.blur();
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === "Enter") {
        setIsOpen(false);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        clearSuggestions();
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showDropdown =
    isOpen && (suggestions.length > 0 || isLoading || Boolean(error));

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (value.length >= 2) {
              fetchSuggestions(value);
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="pl-10 pr-10"
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
          {/*
            Plain scroll container, not <ScrollArea>. Radix's viewport is h-full,
            which resolves to auto against a parent that only has max-height — so
            the list never became scrollable and the last row was clipped instead.
            max-height + overflow-y on one element has no such dependency.
          */}
          <div className="max-h-72 overflow-y-auto overscroll-contain">
            {isLoading && suggestions.length === 0 ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Searching...
              </div>
            ) : error ? (
              // A failed lookup must not read as "there are no such places" —
              // that sent a real outage unnoticed for a long time.
              <div className="py-4 text-center text-sm text-muted-foreground">
                <p className="text-destructive">{error}</p>
                <p className="text-xs">Keep typing to retry</p>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No results found
              </div>
            ) : (
              <div className="py-1">
                {suggestions.map((suggestion, index) => {
                  // For a city the API's text is already "Birmingham, AL", so the
                  // detail line would just repeat it. Only show it when it adds
                  // something the headline doesn't already say.
                  const detail = [
                    suggestion.city,
                    suggestion.state,
                    suggestion.zip,
                  ]
                    .filter(Boolean)
                    .join(", ");
                  const showDetail =
                    suggestion.type !== "zip" &&
                    detail !== "" &&
                    detail !== suggestion.text;

                  return (
                    <button
                      key={suggestion.id}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      type="button"
                      onClick={() => handleSelect(suggestion)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        "w-full px-3 py-2 flex items-start gap-3 text-left transition-colors",
                        highlightedIndex === index
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <span className="mt-0.5 text-muted-foreground">
                        {typeIcons[suggestion.type] || <MapPin className="w-4 h-4" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {suggestion.text}
                        </p>
                        {showDetail && (
                          <p className="text-xs text-muted-foreground truncate">
                            {detail}
                          </p>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {typeLabels[suggestion.type] || suggestion.type}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
